import { useState, useEffect, useCallback, useRef } from 'react';
import { AlertTriangle, Calendar, ChevronDown, Check } from 'lucide-react';
import { api } from '../contexts/AuthContext';

const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export function UnclosedDaysBanner({ branchId, currentDate, onDateSelect, onDataLoaded, className = '' }) {
  const [unclosedDays, setUnclosedDays] = useState([]);
  const [lastCloseDate, setLastCloseDate] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);

  const today = localToday();
  const selectedDate = currentDate || today;

  const fetchUnclosedDays = useCallback(async () => {
    if (!branchId) return;
    try {
      setLoading(true);
      const res = await api.get('/daily-close/unclosed-days', { params: { branch_id: branchId } });
      const days = res.data.unclosed_days || [];
      setUnclosedDays(days);
      setLastCloseDate(res.data.last_close_date);
      if (onDataLoaded) onDataLoaded({ last_close_date: res.data.last_close_date, unclosed_days: days, floor_date: res.data.floor_date });
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => { fetchUnclosedDays(); }, [fetchUnclosedDays]);

  // Close dropdown on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleDateSelect = (date) => {
    setOpen(false);
    if (onDateSelect) onDateSelect(date);
  };

  const pastUnclosed = unclosedDays.filter(d => d.date < today);
  const isEncodingPastDate = selectedDate < today;

  if (loading) return null;
  if (pastUnclosed.length === 0 && !isEncodingPastDate) return null;

  return (
    <div className={`rounded-md border border-amber-200 bg-amber-50/60 ${className}`}>
      <div className="flex items-center justify-between px-3 py-1.5 gap-3">
        {/* Left: icon + message */}
        <div className="flex items-center gap-2 min-w-0">
          <AlertTriangle size={13} className="text-amber-600 shrink-0" />
          <span className="text-[13px] text-amber-800 font-medium truncate" data-testid="unclosed-days-warning">
            {isEncodingPastDate
              ? <>Encoding for <strong>{selectedDate}</strong> — saved to this date</>
              : <>
                  {pastUnclosed.length} unclosed day{pastUnclosed.length > 1 ? 's' : ''}{' '}
                  <span className="font-normal text-amber-600">
                    · {pastUnclosed[0].date}{pastUnclosed.length > 1 ? ` → ${pastUnclosed[pastUnclosed.length - 1].date}` : ''}
                  </span>
                </>
            }
          </span>
          {isEncodingPastDate && (
            <button
              onClick={() => handleDateSelect(today)}
              className="text-[12px] font-semibold underline text-amber-700 hover:text-amber-900 whitespace-nowrap shrink-0"
              data-testid="past-date-indicator"
            >
              Reset to today
            </button>
          )}
        </div>

        {/* Right: Switch date dropdown */}
        {pastUnclosed.length > 0 && (
          <div className="relative flex-shrink-0" ref={dropdownRef}>
            <button
              onClick={() => setOpen(!open)}
              className="flex items-center gap-1.5 h-7 px-2.5 text-xs font-medium rounded-md border transition-colors"
              style={{
                background: isEncodingPastDate ? '#f59e0b' : '#fff',
                color: isEncodingPastDate ? '#fff' : '#92400e',
                borderColor: isEncodingPastDate ? '#d97706' : '#fbbf24'
              }}
              data-testid="unclosed-date-selector"
            >
              <Calendar size={12} />
              <span>Switch date</span>
              <ChevronDown size={12} />
            </button>

            {open && (
              <div
                className="absolute right-0 top-full mt-1 bg-white border border-amber-200 rounded-lg shadow-lg z-50 min-w-[200px] max-h-[280px] overflow-y-auto"
                data-testid="unclosed-date-dropdown"
              >
                <button
                  onClick={() => handleDateSelect(today)}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-amber-50 transition-colors border-b border-amber-100"
                >
                  <span className="font-medium text-slate-700">Today ({today})</span>
                  {selectedDate === today && <Check size={14} className="text-green-600" />}
                </button>
                <div className="px-2 py-1.5">
                  <span className="text-[10px] uppercase tracking-wider text-amber-500 font-semibold">Unclosed Days</span>
                </div>
                {pastUnclosed.map(day => (
                  <button
                    key={day.date}
                    onClick={() => handleDateSelect(day.date)}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-amber-50 transition-colors"
                    data-testid={`select-date-${day.date}`}
                  >
                    <div className="text-left">
                      <span className={`font-medium ${selectedDate === day.date ? 'text-amber-700' : 'text-slate-700'}`}>
                        {day.date}
                      </span>
                      <span className="ml-2 text-xs text-slate-400">
                        {day.sales_count} sales, {day.expense_count} exp
                      </span>
                    </div>
                    {selectedDate === day.date && <Check size={14} className="text-amber-600" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
